import React, { FC } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Divider,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { blueGrey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useAccordion } from "../store/accordion";

const HeadingText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
}));

const SecondaryHeadingText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  color: theme.palette.text.secondary,
}));

const StyledSpan = styled("span")({
  color: "red",
  fontSize: 22,
});

const StyledDiv = styled("div")({
  flexBasis: "33.33%",
});

const styles = {
  details: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
  details2: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
};

interface ReusableAccordianProps {
  children: React.ReactNode;
  accordionName: string;
  heading: string;
  secondaryHeading: string;
  colOrRow?: string;
  required5?: boolean;
  padding?: boolean;
}

const alwaysRequired = [
  "quantity",
  "exteriorClad",
  "stilesRails",
  "glass",
  "headTrack",
  "sill",
  "strip",
  "doorShoeColor",
  "spacer",
  "handle",
  "handleHeight",
  "shootBolt",
  "stickStops",
  "hinge",
  "jamb",
  "sliderConfig",
  "sliderWidth",
  "corner",
  "pivot",
  "pivotHardware",
  // "magnetCatch",
  "squareHinges",
  "aluminumColor",
  "stilesRailsProfile",
  "screen"
];

const ReusableAccordian: FC<ReusableAccordianProps> = ({
  children,
  accordionName,
  heading,
  secondaryHeading,
  colOrRow = "row",
  required5 = false,
  padding = false,
}) => {
  const details = colOrRow === "col" ? styles.details2 : styles.details;

  const handleAccordClick = useAccordion((state) => state.handleAccordClick);
  const accordionState = useAccordion((state) =>
    state.expanded.find((a) => a.name === accordionName)
  );

  const isRequired = required5 ? (
    <StyledSpan>**</StyledSpan>
  ) : accordionState?.require ||
    alwaysRequired.includes(accordionState?.name || "") ? (
    <StyledSpan>*</StyledSpan>
  ) : null;

  return (
    <Accordion
      expanded={accordionState?.expanded || false}
      style={{
        backgroundColor: required5 ? blueGrey[50] : "",
        borderBottom: "1px solid #dcdcdc",
      }}
      onChange={(e) => handleAccordClick(accordionName)}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1c-content"
        id="panel1c-header"
      >
        <StyledDiv>
          <HeadingText>
            {heading} {isRequired}
          </HeadingText>
        </StyledDiv>
        <StyledDiv>
          <SecondaryHeadingText>{secondaryHeading}</SecondaryHeadingText>
        </StyledDiv>
      </AccordionSummary>
      <AccordionDetails
        style={{ paddingBottom: padding ? 40 : "", ...details }}
      >
        {children}
      </AccordionDetails>
      <Divider />
    </Accordion>
  );
};

export default ReusableAccordian;
